// App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import './assets/index.css';

// Context & Hooks
import { useAuth } from './contexts/AuthContext';
import { useRouteData } from './hooks/useRouteData';
import { useLiveBuses } from './hooks/useLiveBuses';
import { useArrivalPoll } from './hooks/useArrivalPoll';
import { useExpectedArrivals } from './hooks/useExpectedArrival';
import { usePersistedState } from './hooks/usePersistedState';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import CookieBanner from './components/common/CookieBanner';

// Main App Views
import RouteVisualiser from './components/visualiser/RouteVisualiser';
import ArrivalsBoard from './components/arrivals/ArrivalsBoard';
import SlidePanel from './components/arrivals/SlidePanel';
import LiveMetricsBoard from './components/metrics/LiveMetricsBoard';
import SubscriptionGuard from './components/common/SubscriptionGuard';
import AdminPanel from './components/admin/AdminPanel';
import About from './pages/About';

// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';

// Legal Pages
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import Cookies from './pages/legal/Cookies';
import GDPR from './pages/legal/GDPR';
import ResetPassword from './pages/ResetPassword';

import * as authApi from '@api/auth';
import { branding } from '@core/config/branding';

// --------------------------------------------------------------
// Auth modal (unchanged)
// --------------------------------------------------------------
function AuthModal({ authMode, onClose, onSwitchMode, onSuccess }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 20,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 420,
          minWidth: 420,
          maxWidth: 'calc(100% - 40px)',
          maxHeight: '90vh',
          overflow: 'auto',
          background: 'var(--bg-surface)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border)',
          flexShrink: 0,
          padding: 0,
        }}
        onClick={e => e.stopPropagation()}
      >
        {authMode === 'login' ? (
          <Login
            onSwitchToRegister={() => onSwitchMode('register')}
            onSuccess={onSuccess}
          />
        ) : (
          <Register
            onSwitchToLogin={() => onSwitchMode('login')}
            onSuccess={onSuccess}
          />
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------
// Main App
// --------------------------------------------------------------
function MainApp() {
  const { isAuthenticated, updateUser, user } = useAuth();
  const [authMode, setAuthMode] = useState('login');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);

  const [dark, setDark] = usePersistedState('dark', false, { storage: 'session' });
  const [view, setView] = usePersistedState('appActiveView', 'visualiser', { storage: 'session' });
  const [arrivalDirection, setArrivalDirection] = usePersistedState('arrivalDirection', 'outbound');

  const { routes, selectedRouteId, setSelectedRouteId, stops, loading, error } = useRouteData();
  const { buses, loading: liveLoading, error: liveError, lastUpdated: liveLastUpdated } = useLiveBuses(selectedRouteId);
  const { stops: tableStops, trips, rawArrivals, loading: arrivalsLoading } = useArrivalPoll(selectedRouteId, arrivalDirection);
  const { data: expectedArrivals, loading: expectedLoading } = useExpectedArrivals(selectedRouteId);

  // Apply dark mode class on the document
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  // Restore selected route from localStorage when routes load
  useEffect(() => {
    if (routes.length > 0 && !selectedRouteId) {
      const saved = localStorage.getItem('selectedRouteId');
      let restored = null;
      if (saved) {
        try {
          restored = JSON.parse(saved);
        } catch {
          restored = saved;
        }
      }
      if (restored && routes.some(r => r.id === restored)) {
        setSelectedRouteId(restored);
      }
    }
  }, [routes, selectedRouteId, setSelectedRouteId]);

  // Persist selected route
  useEffect(() => {
    if (selectedRouteId) {
      localStorage.setItem('selectedRouteId', JSON.stringify(selectedRouteId));
    }
  }, [selectedRouteId]);

  // Auth modal handlers
  const handleLoginClick = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };

  // Login/Register already call `login()` before invoking onSuccess.
  // This only fetches the account to populate isAdmin and closes the modal.
  const handleAuthSuccess = async (token) => {
    try {
      const data = await authApi.fetchCurrentUser(token);
      updateUser({ email: data.email, isAdmin: !!data.is_admin });
    } catch (e) {
      console.error('Failed to fetch user after login', e);
    }
    setShowAuthModal(false);
  };

  const handleStopSelect = (stop) => {
    setSelectedStop(stop);
  };

  const renderContent = () => {
    if (view === 'visualiser') {
      return (
        <RouteVisualiser
          stops={stops}
          buses={buses}
          setSelectedStop={handleStopSelect}
          expectedArrivals={expectedArrivals}
        />
      );
    }
    if (view === 'arrivals') {
      return (
        <SubscriptionGuard
          routeId={selectedRouteId}
          tier="arrival"
          featureName="Arrivals Board"
          description="Unlock full-day arrival times and live headway for every bus on this route."
          corporateNotice={branding.corporateNotice}
          onLoginClick={handleLoginClick}
        >
          <ArrivalsBoard
            stops={tableStops}
            trips={trips}
            rawArrivals={rawArrivals}
            setSelectedStop={handleStopSelect}
            activeDir={arrivalDirection}
            onDirChange={setArrivalDirection}
            routeId={selectedRouteId}
            expectedArrivals={expectedArrivals}
          />
        </SubscriptionGuard>
      );
    }
    if (view === 'metrics') {
      return (
        <SubscriptionGuard
          routeId={selectedRouteId}
          tier="metrics"
          featureName="Live Metrics"
          description="Unlock live EWT, real-time OTP. Instant performance insights for this route today."
          corporateNotice={branding.corporateNotice}
          onLoginClick={handleLoginClick}
        >
          <LiveMetricsBoard routeId={selectedRouteId} stops={stops} />
        </SubscriptionGuard>
      );
    }
    if (view === 'admin') {
      return <AdminPanel />;
    }
    return null;
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Navbar
          dark={dark}
          onToggleDark={() => setDark(d => !d)}
          routes={routes}
          selectedRoute={selectedRouteId}
          onRouteChange={setSelectedRouteId}
          view={view}
          onViewChange={setView}
          isAuthenticated={isAuthenticated}
          onLoginClick={handleLoginClick}
          isAdmin={user?.isAdmin}
          userEmail={user?.email}
          dataLoading={loading || liveLoading || arrivalsLoading || expectedLoading}
          liveLoading={liveLoading}
          liveError={liveError}
          liveLastUpdated={liveLastUpdated}
        />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {loading ? (
            <div style={{ display: 'grid', placeItems: 'center', flex: 1, color: 'var(--text-muted)' }}>
              Loading route data...
            </div>
          ) : error ? (
            <div style={{ display: 'grid', placeItems: 'center', flex: 1, color: 'var(--live-red)' }}>
              {error}
            </div>
          ) : (
            renderContent()
          )}
        </main>

        <SlidePanel
          open={!!selectedStop}
          stopData={selectedStop}
          onClose={() => setSelectedStop(null)}
          routeId={selectedRouteId}
          isAuthenticated={isAuthenticated}
          onLoginClick={handleLoginClick}
        />
      </div>

      {showAuthModal && (
        <AuthModal
          authMode={authMode}
          onClose={handleCloseAuthModal}
          onSwitchMode={setAuthMode}
          onSuccess={handleAuthSuccess}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------
// Global Layout (Footer, CookieBanner, and page wrapper)
// --------------------------------------------------------------
function Layout() {
  return (
    <div className="flex flex-col h-screen">
      <Outlet />
      <Footer />
      <CookieBanner />
    </div>
  );
}

// --------------------------------------------------------------
// Root App with Router
// --------------------------------------------------------------
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<MainApp />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/gdpr" element={<GDPR />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/about" element={<About />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}